import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

/**
 * Реестр прямоугольников для `Hero` из Flutter.
 *
 * Во Flutter `Hero` — это дело навигатора: он находит на двух страницах
 * виджеты с одним `tag` и на время перехода перекладывает один в другой
 * через overlay. В вебе навигатора с overlay нет, поэтому перелёт делается
 * приёмом FLIP: тот, кто уже был на экране, оставляет здесь свой
 * прямоугольник, а новый виджет с тем же тегом стартует из него.
 *
 * Провайдер вешается один раз над роутером — иначе [MaybeHero] просто
 * отдаст ребёнка без анимации.
 */
export interface HeroRegistry {
  /** Прямоугольник виджета с этим тегом или `null`, если его не было. */
  read(tag: string): DOMRect | null;
  write(tag: string, rect: DOMRect): void;
}

const HeroRegistryContext = createContext<HeroRegistry | null>(null);

export interface HeroProviderProps {
  readonly children: ReactNode;
}

export function HeroProvider({ children }: HeroProviderProps) {
  const rectsRef = useRef<ReadonlyMap<string, DOMRect>>(new Map());
  const registry = useMemo<HeroRegistry>(
    () => ({
      read: (tag) => rectsRef.current.get(tag) ?? null,
      // Карта не меняется на месте: каждая запись — новая карта.
      write: (tag, rect) => {
        rectsRef.current = new Map<string, DOMRect>([
          ...rectsRef.current,
          [tag, rect],
        ]);
      },
    }),
    [],
  );
  return (
    <HeroRegistryContext.Provider value={registry}>
      {children}
    </HeroRegistryContext.Provider>
  );
}

export function useHeroRegistry(): HeroRegistry | null {
  return useContext(HeroRegistryContext);
}
